import * as React from 'react';
import * as PDFJS from 'pdfjs-dist';
import Button from 'common/components/Button';
import IconFont from 'common/components/IconFont';
import { ConfigConsumer, ConfigConsumerProps } from '../Page/Core';
import Formatter from 'common/utils/formatter';
import './style.less';

interface PDFPromise<T> {
    isResolved: () => boolean;
    isRejected: () => boolean;
    resolve: (value: T) => void;
    reject: (reason: string) => void;
    then: <U>(onResolve: (promise: T) => U, onReject?: (reason: string) => void) => PDFPromise<U>;
}

interface PDFDoc {
    numPages: number;
    fingerprint: string;
    view: Array<number>;
    embeddedFontsUsed: () => boolean;
    getPage: (n: number) => PDFPromise<any>;
    getViewport: (n: number) => any;
    getJavaScript: () => PDFPromise<string[]>;
    getOutline: () => PDFPromise<Array<any>>;
    getMetadata: () => PDFPromise<{ info: any; metadata: any }>;
    render: (args: any) => void;
    destroy: () => void;
}

export interface PDFReaderProps {
    src: string;
    hideButton?: boolean;
    scale?: number;
    style?: any;
    overlayStyle?: any;
    check?: boolean;
}

export interface PDFReaderState {
    pdf: PDFDoc;
    page: number;
    scale: number;
    imgOperationShow: boolean;
}

/**
 * PDFReader
 */
export default class PDFReader extends React.Component<PDFReaderProps, PDFReaderState> {
    private mounted: boolean;
    private doc: PDFDoc;
    private core: HTMLCanvasElement;
    private initialSrc: string;
    private static scale: any = {
        zoom: 1.2,
        minify: 0.8
    };
    private static page: any = {
        next: 1,
        prev: -1
    };

    constructor(props: PDFReaderProps) {
        super(props);
        this.state = {
            pdf: null,
            page: 1,
            scale: 1,
            imgOperationShow: false
        };
        // load web worker
        if (typeof window !== 'undefined' && 'Worker' in window) {
            PDFJS.GlobalWorkerOptions.workerPort = new Worker(new URL('pdfjs-dist/build/pdf.worker.min', import.meta.url),);
        }
    }

    getSrc = (props: any) => Formatter.contact.relativeUrl(props.src);

    componentDidMount() {
        this.mounted = true;
        const src = this.getSrc(this.props);
        if (!src) return;
        PDFJS.getDocument({ url: src }).promise.then((pdf: PDFDoc) => {
            this.doc && this.doc.destroy();
            this.initialSrc = src;
            this.doc = pdf;
            pdf.getPage(this.state.page).then(p => this.drawPDF(p, PDFReader.scale.normal));
            this.mounted && this.setState({ pdf });
        });
    }
    UNSAFE_componentWillReceiveProps(nexProps: any) {
        const src = this.getSrc(this.props);
        if (!src) return;
        if (this.props.check && src === this.props.src) return;
        PDFJS.getDocument({ url: src }).promise.then((pdf: PDFDoc) => {
            this.doc && this.doc.destroy();
            this.initialSrc = src;
            this.doc = pdf;
            pdf.getPage(this.state.page).then(p => this.drawPDF(p, PDFReader.scale.normal));
            this.setState({ pdf });
        });
    }
    componentWillUnmount() {
        this.mounted = false;
        this.doc && this.doc.destroy();
    }

    // draw file
    private drawPDF = (page: PDFDoc, scale: number = 1) => {
        let originViewport = page.getViewport(1);                  // pdf 文件宽度
        let offsetWidth = this.core.parentElement.offsetWidth;        // 容器宽度
        let multiple = offsetWidth / originViewport.width * scale;    // 尺寸缩放比例
        let targetViewport = page.getViewport(multiple);
        let canvasContext = this.core.getContext('2d');
        this.core.height = targetViewport.height;
        this.core.width = targetViewport.width;
        page.render({ canvasContext, viewport: targetViewport });
    };

    // scale
    private scale = (multiple: number) => () => {
        const { pdf, scale } = this.state;
        if (!pdf) return;
        const deal = scale * multiple;
        pdf.getPage(this.state.page).then((p: PDFDoc) => this.drawPDF(p, deal));
        this.mounted && this.setState({ scale: deal });
    };

    // page change
    private pagination = (step: number) => () => {
        const pdf = this.pdf;
        if (!pdf) return;
        const { lastPage, next, prev, core } = pdf;
        if (!next && !prev) return;
        const currPage = lastPage + step;
        core.getPage(currPage).then((p: PDFDoc) => this.drawPDF(p));
        this.mounted && this.setState({ page: currPage });
    };

    // reset canvas
    private reset = () => {
        const { pdf, page } = this.state;
        pdf.getPage(page).then((p: PDFDoc) => this.drawPDF(p, 1));
        this.mounted && this.setState({ scale: 1 });
    };

    // pagination disable
    private get pdf() {
        const { pdf, page } = this.state;
        if (!pdf) return null;
        const maxPage = pdf.numPages;
        const src = this.getSrc(this.props);
        return {
            maxPage,
            lastPage: page,
            next: maxPage > page,
            prev: page > 1,
            reset: !!this.initialSrc && src === this.initialSrc,
            core: pdf
        };
    }
    getImgOver = () => {
        const src = this.getSrc(this.props);
        return (
            <div className='img-operation'>
                <a href={`/v2/common/file/download?uri=${src}`}>
                    <IconFont type="download" style={{ color: '#eee', fontSize: 24 }} />
                </a>
                <a href={src} target='_blank'>
                    <IconFont type="eye" style={{ color: '#eee', fontSize: 24 }} />
                </a>

            </div>
        );
    }

    // render content
    private renderCanvas = ({ getPrefixCls }: ConfigConsumerProps) => {
        const { style, overlayStyle, hideButton, check } = this.props;
        const { imgOperationShow } = this.state;
        const pdf = this.pdf;
        // const maxWidth = (overlayStyle && overlayStyle.maxWidth) || this.core && this.core.parentElement.offsetWidth;
        const overflowX = this.state.scale > 1 ? 'auto' : 'hidden';
        return (
            <div className={getPrefixCls('pdf-file')} style={style}>
                {!!pdf && !hideButton && <p className={getPrefixCls('pdf-pagination')}>
                    <Button disabled={!pdf.prev} onClick={this.pagination(PDFReader.page.prev)}>
                        <IconFont type="Left" />
                    </Button>
                    <Button disabled={!pdf.next} onClick={this.pagination(PDFReader.page.next)}>
                        <IconFont type="right" />
                    </Button>
                    <Button onClick={this.scale(PDFReader.scale.zoom)}><IconFont type="plus" /></Button>
                    <Button onClick={this.scale(PDFReader.scale.minify)}><IconFont type="minus" /></Button>
                    <Button disabled={!pdf.reset} onClick={this.reset}><IconFont type="undo" /></Button>
                </p>}
                <div style={{ ...overlayStyle, overflowX }} className='img-container'
                    onMouseOver={() => this.setState({ imgOperationShow: true })}
                    onMouseLeave={() => this.setState({ imgOperationShow: false })}
                >
                    {
                        check && <div className='img-over' style={{
                            display: imgOperationShow ? 'block' : 'none',
                            width: this.core ? this.core.width : 'auto', height: this.core ? this.core.height : 'auto'
                        }}>
                            {this.getImgOver()}
                        </div>
                    }
                    <div>
                        <canvas ref={(ctx: HTMLCanvasElement) => this.core = ctx} />
                    </div>

                </div>
            </div>
        );
    };

    render() {
        return <ConfigConsumer>{this.renderCanvas}</ConfigConsumer>;
    }
}
